import { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Prompt, PromptGroup_deprecated, PromptGroup } from '@server/prompts';
import {
  selectPromptGroups,
  selectPrompts,
  selectPromptGroupsWithPrompts_deprecated,
  selectSelectedPromptGroupId,
  setSelectedPromptGroupId,
} from '../../../reducers/promptLibraryReducer';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { PromptWithCategory } from './types';

const EMPTY_CATEGORIES: PromptGroup_deprecated[] = [];
const EMPTY_PROMPTS: Prompt[] = [];

export const UNCATEGORIZED_FILTER_ID = '__uncategorized__';

export const usePromptGroupState = () => {
  const dispatch = useDispatch();
  const promptGroupsFromStore = useSelector(selectPromptGroups);
  const promptsFromStore = useSelector(selectPrompts) ?? EMPTY_PROMPTS;
  const categoriesFromStore_deprecated =
    useSelector(selectPromptGroupsWithPrompts_deprecated) ?? EMPTY_CATEGORIES;
  const selectedPromptGroupIdFromStore = useSelector(selectSelectedPromptGroupId);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const { promptsLibraryV2 } = useFeatureToggle();

  // For V2: use Redux state, for V1 filters: this is managed separately
  const selectedCategoryId = selectedPromptGroupIdFromStore;

  // For V2: use flat prompt groups without nested prompts
  // For V1: use deprecated structure with nested prompts
  const categories: (PromptGroup_deprecated | PromptGroup)[] = useMemo(() => {
    if (promptsLibraryV2 && promptGroupsFromStore) {
      return [...promptGroupsFromStore].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }
    return categoriesFromStore_deprecated;
  }, [promptGroupsFromStore, categoriesFromStore_deprecated, promptsLibraryV2]);

  // Map of category id to category for quick lookup
  const categoriesById = useMemo(() => {
    return new Map(categories.map((cat) => [cat.id, cat]));
  }, [categories]);

  // For V2: derive from flat prompts list
  // For V1: derive from nested structure
  const allPrompts: PromptWithCategory[] = useMemo(() => {
    if (promptsLibraryV2) {
      return promptsFromStore
        .filter((prompt) => prompt.id !== '')
        .map((prompt) => {
          const category = prompt.promptGroupId ? categoriesById.get(prompt.promptGroupId) : null;
          return {
            ...prompt,
            categoryName: category?.name ?? '',
            categoryId: prompt.promptGroupId ?? '',
          };
        });
    }

    // Deprecated: derive from nested structure
    return (categoriesFromStore_deprecated as PromptGroup_deprecated[]).flatMap((category) =>
      category.prompts.map((prompt) => ({
        ...prompt,
        categoryName: category.name,
        categoryId: category.id,
      })),
    );
  }, [promptsLibraryV2, promptsFromStore, categoriesById, categoriesFromStore_deprecated]);

  // Prompts without a group
  const uncategorizedPrompts: PromptWithCategory[] = useMemo(() => {
    return allPrompts.filter((prompt) => !prompt.promptGroupId);
  }, [allPrompts]);

  const filteredPrompts: PromptWithCategory[] = useMemo(() => {
    if (!selectedCategoryId) {
      return allPrompts;
    }
    if (selectedCategoryId === UNCATEGORIZED_FILTER_ID) {
      return uncategorizedPrompts;
    }
    return allPrompts.filter((prompt) => prompt.categoryId === selectedCategoryId);
  }, [allPrompts, selectedCategoryId, uncategorizedPrompts]);

  const totalPromptsCount = allPrompts.length;

  const handleCategorySelect = useCallback(
    (categoryId: string | null) => {
      dispatch(setSelectedPromptGroupId({ groupId: categoryId }));
    },
    [dispatch],
  );

  const handlePromptSelect = useCallback((prompt: Prompt | null) => {
    setSelectedPrompt(prompt);
  }, []);

  const clearSelectedCategoryIfDeleted = useCallback(
    (deletedCategoryId: string) => {
      if (selectedCategoryId === deletedCategoryId) {
        dispatch(setSelectedPromptGroupId({ groupId: null }));
      }
    },
    [selectedCategoryId, dispatch],
  );

  return {
    categories,
    allPrompts,
    uncategorizedPrompts,
    filteredPrompts,
    totalPromptsCount,

    selectedCategoryId,
    selectedPrompt,
    handleCategorySelect,
    handlePromptSelect,
    clearSelectedCategoryIfDeleted,
  };
};
